/**
 * Password validation utility following OWASP guidelines
 * 
 * OWASP Recommendations:
 * - Minimum 12 characters (increased from 8)
 * - Mix of uppercase, lowercase, numbers, and special characters
 * - Consider password strength meter
 * - Consider breach detection (future enhancement)
 * 
 * Reference: OWASP Password Storage Cheat Sheet
 */

export interface PasswordValidationResult {
  isValid: boolean;
  minLength: boolean;
  hasUpperCase: boolean;
  hasLowerCase: boolean;
  hasNumbers: boolean;
  hasSpecialChar: boolean;
  isNotEasilyGuessable: boolean;
  strength: 'weak' | 'medium' | 'strong' | 'very-strong';
  score: number; // 0-100
}

/**
 * Checks if password contains month/year patterns
 * @param password - Password to check
 * @returns true if password contains month/year pattern
 */
function containsMonthYearPattern(password: string): boolean {
  const normalizedPassword = password.toLowerCase();
  
  // Month names (full and abbreviated)
  const months = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december',
    'jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'sept', 'oct', 'nov', 'dec'
  ];
  
  // Check for month followed by year (4 digits, e.g., 2024, 2025)
  for (const month of months) {
    // Pattern: month + year (e.g., "january2024", "jan2024")
    if (normalizedPassword.includes(month) && /\d{4}/.test(password)) {
      return true;
    }
    // Pattern: month + separator + year (e.g., "jan/2024", "jan-2024", "jan_2024")
    if (new RegExp(`${month}[\\/\\-\\_]\\d{4}`, 'i').test(password)) {
      return true;
    }
    // Pattern: year + month (e.g., "2024jan", "2024/jan")
    if (new RegExp(`\\d{4}[\\/\\-\\_]?${month}`, 'i').test(password)) {
      return true;
    }
  }
  
  // Check for MM/YYYY, MM-YYYY, MM_YYYY patterns (e.g., "01/2024", "12-2024")
  if (/\b(0[1-9]|1[0-2])[\/\-_]\d{4}\b/.test(password)) {
    return true;
  }
  
  // Check for YYYY/MM, YYYY-MM, YYYY_MM patterns (e.g., "2024/01", "2024-12")
  if (/\b\d{4}[\/\-_](0[1-9]|1[0-2])\b/.test(password)) {
    return true;
  }
  
  return false;
}

/**
 * Checks if password contains common easily guessable patterns
 * @param password - Password to check
 * @returns true if password contains easily guessable patterns
 */
function containsEasilyGuessablePattern(password: string): boolean {  
  // Common passwords with character substitutions
  const commonPatterns = [
    // Password variations with substitutions
    /p[@a4]ssw[o0]rd/i,
    /p[a4]ssw[o0]rd/i,
    /p[a4]55w[o0]rd/i,
    /p[a4]ssw[o0]rd123/i,
    /password123/i,
    /password1/i,
    /passw0rd/i,
    /p@ssw0rd/i,
    /p@55w0rd/i,
    
    // Common weak passwords
    /qwerty/i,
    /12345678/i,
    /abcdefgh/i,
    /welcome123/i,
    /admin123/i,
    /letmein/i,
    /monkey/i,
    /dragon/i,
    /master/i,
    /sunshine/i,
    /princess/i,
    /football/i,
    /iloveyou/i,
    
    // Sequential patterns (3+ consecutive)
    /(012|123|234|345|456|567|678|789|890)/,
    // /(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i,
    /(qwer|wert|erty|rtyu|tyui|yuio|uiop)/i,
    /(asdf|sdfg|dfgh|fghj|ghjk|hjkl)/i,
    /(zxcv|xcvb|cvbn|vbnm)/i,
    
    // Repeated characters (4+ same character)
    /(.)\1{3,}/,
    
    // Common keyboard patterns
    /qwerty/i,
    /asdfgh/i,
    /zxcvbn/i,
  ];
  
  // Check against common patterns
  for (const pattern of commonPatterns) {
    if (pattern.test(password)) {
      return true;
    }
  }
  
  // Check for month/year patterns
  if (containsMonthYearPattern(password)) {
    return true;
  }
  
  return false;
}

/**
 * Validates password according to OWASP guidelines
 * @param password - Password to validate
 * @returns PasswordValidationResult with detailed validation information
 */
export function validatePassword(password: string): PasswordValidationResult {
  // OWASP recommendation: Minimum 12 characters
  const minLength = 12;
  
  // Character type validations
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  // OWASP: Include special characters for better security
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>\[\]\\\/_+\-=~`]/.test(password);
  const minLengthValid = password.length >= minLength;
  
  // Check for easily guessable patterns
  const isNotEasilyGuessable = !containsEasilyGuessablePattern(password);
  
  // All requirements must be met (including not easily guessable)
  const isValid = minLengthValid && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar && isNotEasilyGuessable;
  
  // Calculate password strength score (0-100)
  let score = 0;
  
  // Length scoring (0-40 points)
  if (password.length >= minLength) score += 20;
  if (password.length >= 16) score += 10;
  if (password.length >= 20) score += 10;
  
  // Character variety scoring (0-40 points)
  if (hasUpperCase) score += 10;
  if (hasLowerCase) score += 10;
  if (hasNumbers) score += 10;
  if (hasSpecialChar) score += 10;
  
  // Complexity bonus (0-20 points)
  // Check for mixed case, numbers, and special chars together
  if (hasUpperCase && hasLowerCase) score += 5;
  if (hasNumbers && hasSpecialChar) score += 5;
  if (password.length >= 14 && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar) score += 10;
  
  // Penalize easily guessable passwords (reduce score significantly)
  if (!isNotEasilyGuessable) {
    score = Math.max(0, score - 50); // Heavy penalty for easily guessable passwords
  }
  
  // Determine strength level
  let strength: 'weak' | 'medium' | 'strong' | 'very-strong';
  if (score < 40) {
    strength = 'weak';
  } else if (score < 60) {
    strength = 'medium';
  } else if (score < 80) {
    strength = 'strong';
  } else {
    strength = 'very-strong';
  }
  
  return {
    isValid,
    minLength: minLengthValid,
    hasUpperCase,
    hasLowerCase,
    hasNumbers,
    hasSpecialChar,
    isNotEasilyGuessable,
    strength,
    score,
  };
}

/**
 * Gets user-friendly password requirements message
 * @returns Array of requirement strings
 */
export function getPasswordRequirements(): string[] {
  return [
    'At least 12 characters (OWASP recommended)',
    'One uppercase letter',
    'One lowercase letter',
    'One number',
    'One special character (!@#$%^&*(),.?":{}|<>[]\\/_+-=~`)',
    'Cannot contain easily guessable patterns (e.g., month/year, common passwords like Pa55word, sequential patterns)',
  ];
}

/**
 * Gets password strength color for UI display
 * @param strength - Password strength level
 * @returns Tailwind CSS color class
 */
export function getPasswordStrengthColor(strength: PasswordValidationResult['strength']): string {
  switch (strength) {
    case 'weak':
      return 'text-red-500';
    case 'medium':
      return 'text-yellow-500';
    case 'strong':
      return 'text-green-500';
    case 'very-strong':
      return 'text-green-600';
    default:
      return 'text-gray-500';
  }
}

/**
 * Gets password strength label for UI display
 * @param strength - Password strength level
 * @returns Human-readable strength label
 */
export function getPasswordStrengthLabel(strength: PasswordValidationResult['strength']): string {
  switch (strength) {
    case 'weak':
      return 'Weak';
    case 'medium':
      return 'Medium';
    case 'strong':
      return 'Strong';
    case 'very-strong':
      return 'Very Strong';
    default:
      return 'Unknown';
  }
}

